// 订单详情页
import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
  Container, Box, Card, CardContent, Typography, Button, Stack,
  Chip, Divider, CircularProgress, Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RlPageHeader from '../../components/real-lottery/RlPageHeader';
import { BallDisplay } from '../../components/real-lottery/BallPicker';
import { getRlOrder, refreshRlOrder, RL_STATUS_TEXT, RL_STATUS_COLOR } from '../../services/realLotteryApi';
import { getCurrentRlUser } from '../../components/real-lottery/useRlUser';

export default function RealLotteryOrderDetailPage() {
  const history = useHistory();
  const { outTradeNo } = useParams();
  const location = useLocation();
  const isFresh = new URLSearchParams(location.search).get('fresh') === '1';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    const u = getCurrentRlUser();
    if (!u) { history.replace('/login-v2'); return; }
    const res = await getRlOrder(outTradeNo, u.userId);
    if (res.success) setOrder(res.data);
    else setError(res.message || '订单不存在');
    setLoading(false);
  };

  useEffect(() => {
    load();
    // 新下单：自动 5 秒刷新一次最多 3 次（等出票回调）
    if (isFresh) {
      let count = 0;
      const t = setInterval(async () => {
        count++;
        await load();
        if (count >= 3) clearInterval(t);
      }, 5000);
      return () => clearInterval(t);
    }
  }, [outTradeNo]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshRlOrder(outTradeNo);
    await load();
    setRefreshing(false);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!order) {
    return (
      <Box>
        <RlPageHeader title="订单详情" />
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Alert severity="error">{error || '订单不存在'}</Alert>
        </Container>
      </Box>
    );
  }

  const statusText = order.statusText || RL_STATUS_TEXT[order.status];
  const statusColor = RL_STATUS_COLOR[order.status] || 'default';
  const isWon = order.status === 'won';
  const isRedeemFailed = order.status === 'redeem_failed';

  return (
    <Box sx={{ backgroundColor: '#f5f5f7', minHeight: '100vh', pb: 12 }}>
      <RlPageHeader
        title="订单详情"
        right={
          <Button onClick={handleRefresh} disabled={refreshing} startIcon={<RefreshIcon />} size="small">
            {refreshing ? '刷新中' : '刷新'}
          </Button>
        }
      />
      <Container maxWidth="sm" sx={{ pt: 2 }}>
        {/* 状态卡 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{statusText}</Typography>
              <Chip label={statusText} color={statusColor} size="small" />
            </Stack>
            {order.failReason && (
              <Alert severity="warning" sx={{ mt: 1 }}>{order.failReason}</Alert>
            )}
          </CardContent>
        </Card>

        {/* 投注信息 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>投注信息</Typography>
            <Stack spacing={1}>
              <Row label="玩法" value={order.gameCode === 'ssq' ? '双色球' : order.gameCode} />
              <Row label="期号" value={`第 ${order.period} 期`} />
              <Row label="注数 / 倍数" value={`${order.betCount} 注 × ${order.multiple} 倍`} />
              <Row label="消耗积分" value={`${order.costPoints} 分`} />
              <Box>
                <Typography variant="caption" color="text.secondary">投注号码</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <BallDisplay red={order.redBalls} blue={order.blueBall} size={30} />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 开奖信息 */}
        {order.drawRedBalls && order.drawRedBalls.length > 0 && (
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>开奖结果</Typography>
              <BallDisplay red={order.drawRedBalls} blue={order.drawBlueBall} size={30} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                命中：红 {order.matchRed} · 蓝 {order.matchBlue}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* 中奖卡 */}
        {(isWon || order.status === 'redeeming' || order.status === 'redeemed' || isRedeemFailed) && (
          <Card sx={{ mb: 2, borderRadius: 2,
            background: 'linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%)' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">中奖金额</Typography>
              <Typography variant="h4" color="error" sx={{ fontWeight: 700, my: 1 }}>
                ¥{order.afterTaxAmountYuan}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                税前 ¥{order.winAmountYuan}
                {order.taxAmountFen > 0 && ` · 已扣偶然所得税 ¥${(order.taxAmountFen / 100).toFixed(2)} (20%)`}
              </Typography>
              {order.isHugeWin && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  单注奖金 ≥ 10 万元，请联系客服线下兑奖
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* 兑奖信息 */}
        {(order.status === 'redeeming' || order.status === 'redeemed' || isRedeemFailed) && (
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>兑奖信息</Typography>
              <Stack spacing={1}>
                <Row label="收款账号" value={order.redeemAccount} />
                <Row label="收款人" value={order.redeemName} />
                <Row label="渠道" value="支付宝" />
                <Row label="提交时间" value={order.redeemAt ? new Date(order.redeemAt).toLocaleString('zh-CN') : '-'} />
                {order.redeemPaidAt && (
                  <Row label="打款时间" value={new Date(order.redeemPaidAt).toLocaleString('zh-CN')} />
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* 元信息 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>订单信息</Typography>
            <Stack spacing={0.5}>
              <Row label="订单号" value={order.outTradeNo} small />
              {order.gnwxOrderNo && <Row label="平台订单号" value={order.gnwxOrderNo} small />}
              <Row label="下单时间" value={order.createdAt ? new Date(order.createdAt).toLocaleString('zh-CN') : '-'} small />
              {order.ticketedAt && (
                <Row label="出票时间" value={new Date(order.ticketedAt).toLocaleString('zh-CN')} small />
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>

      {(isWon || isRedeemFailed) && !order.isHugeWin && (
        <Box sx={{
          position: 'fixed', left: 0, right: 0, bottom: 0,
          backgroundColor: '#fff', borderTop: '1px solid rgba(0,0,0,0.08)',
          py: 1.5, px: 2, zIndex: 10,
        }}>
          <Container maxWidth="sm" disableGutters>
            <Button
              fullWidth variant="contained" color="success" size="large"
              onClick={() => history.push(`/real-lottery/redeem/${order.outTradeNo}`)}
              sx={{ py: 1.3 }}
            >
              立即兑奖（¥{order.afterTaxAmountYuan}）
            </Button>
          </Container>
        </Box>
      )}
    </Box>
  );
}

function Row({ label, value, small }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant={small ? 'caption' : 'body2'} color="text.secondary">{label}</Typography>
      <Typography variant={small ? 'caption' : 'body2'} sx={{ fontWeight: small ? 400 : 500 }}>{value}</Typography>
    </Stack>
  );
}
